import React, { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { usePreloadedQuery } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import type { RelayError } from '@confluence/relay-errors/entry-points/RelayError.ts';

import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery,
	type SpaceNavigationQuery$data,
	type SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { SpaceNavigationQueryContext } from './SpaceNavigationQueryContext.ts';
import {
	getSpaceNavigationQueryOwner,
	getOrCreateSpaceNavigationQueryOwner,
} from './SpaceNavigationQueryOwner.ts';
import type { SpaceNavigationQueryReference } from './SpaceNavigationQueryReference.ts';
import { ContainerSkeleton } from './ContainerSkeleton.tsx';

type QueryResult = {
	data?: SpaceNavigationQuery$data;
	error?: RelayError;
	variables: SpaceNavigationQuery$variables;
};
type Props = {
	environment: IEnvironment;
	variables: SpaceNavigationQuery$variables;
	fallback: ReactNode;
	children: (result: QueryResult) => ReactNode;
};

const canRetainNavigation = (
	reference: SpaceNavigationQueryReference | undefined,
	environment: IEnvironment,
	variables: SpaceNavigationQuery$variables,
) =>
	reference &&
	!reference.disposed &&
	reference.environment === environment &&
	reference.variables.spaceKey === variables.spaceKey &&
	reference.variables.cloudId === variables.cloudId &&
	reference.variables.isLicensed === variables.isLicensed &&
	(reference.variables.includePersistentInviteLicense ?? false) ===
		(variables.includePersistentInviteLicense ?? false);

/**
 * The route prepares the reference. Uncovered mounts first use their existing reader;
 * an effect can adopt that in-flight execution without starting work during render.
 */
export const SpaceNavigationQueryReader: FC<Props> = ({
	environment,
	variables,
	fallback,
	children,
}) => {
	const [loaded, setLoaded] = useState<SpaceNavigationQueryReference>();
	const prepared = getSpaceNavigationQueryOwner(environment)?.get(variables);
	const requested =
		prepared ?? (canRetainNavigation(loaded, environment, variables) ? loaded : undefined);
	const deferred = useDeferredValue(requested);
	const displayed = canRetainNavigation(deferred, environment, variables) ? deferred : requested;

	useEffect(() => {
		const owner = getOrCreateSpaceNavigationQueryOwner(environment);
		const reference = owner.get(variables) ?? owner.preload(variables);
		const release = owner.retain(reference);
		startTransition(() => setLoaded(reference));
		return release;
	}, [environment, variables]);
	useEffect(() => {
		if (displayed) return getSpaceNavigationQueryOwner(environment)?.retain(displayed);
	}, [environment, displayed]);

	return (
		<RelaySuspense
			key={`${variables.cloudId}:${variables.spaceKey}:${variables.isLicensed}:${variables.includePersistentInviteLicense ?? false}`}
			interactionName="SpaceNavigationQueryOwner"
			fallback={<ContainerSkeleton loader />}
		>
			{displayed && !displayed.disposed ? (
				<QueryReferenceReader reference={displayed}>{children}</QueryReferenceReader>
			) : (
				fallback
			)}
		</RelaySuspense>
	);
};

const QueryReferenceReader = ({
	reference,
	children,
}: {
	reference: SpaceNavigationQueryReference;
	children: Props['children'];
}) => {
	if (reference.failed) {
		return <>{children({ error: reference.error, variables: reference.variables })}</>;
	}
	// Native Relay controls suspension, including SSR blocked-query handoff.
	return <PreloadedNavigation reference={reference}>{children}</PreloadedNavigation>;
};

const PreloadedNavigation = ({
	reference,
	children,
}: {
	reference: SpaceNavigationQueryReference;
	children: Props['children'];
}) => {
	const data = usePreloadedQuery<SpaceNavigationQuery>(
		SpaceNavigationQueryArtifact,
		reference.reference,
	);
	const context = useMemo(
		() => ({ data, variables: reference.variables }),
		[data, reference.variables],
	);
	return (
		<SpaceNavigationQueryContext.Provider value={context}>
			{children({ ...context, error: reference.error })}
		</SpaceNavigationQueryContext.Provider>
	);
};
